import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import { searchClientes } from '@/api/clientes';
import { buscarTpu, type CondicaoRegra } from '@/api/regrasDistribuicao';
import { Opcao } from '@/components/escritorio/DestinosPicker';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

import { CAMPOS, JUSTICAS, ORIGENS, POLOS, ROTULO_DO_CAMPO, rotuloDoValor, UFS } from './rotulos';

interface CondicaoEditorProps {
  id: string;
  cond: CondicaoRegra;
  onChange: (c: CondicaoRegra) => void;
  onRemover: () => void;
  membros: EscritorioMembro[];
  /** Acabou de entrar: o cursor vai para o primeiro campo dele. */
  focar?: boolean;
  /** O gestor tentou salvar com o critério sem valor. */
  incompleto?: boolean;
  /** O critério da IA que a regra já tinha (mudar o texto faz a IA avaliar de novo). */
  iaOriginal?: string;
}

/** Um critério da regra e os valores dele (qualquer um casa). */
export function CondicaoEditor({
  id,
  cond,
  onChange,
  onRemover,
  membros,
  focar,
  incompleto,
  iaOriginal,
}: CondicaoEditorProps) {
  const ajuda = CAMPOS.find((c) => c.campo === cond.campo)?.ajuda;
  const valoresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focar) return;
    const caixa = valoresRef.current;
    // O campo de texto antes dos botões: em "Parte (nome)" o polo vem antes, mas o que se
    // digita é o nome.
    const alvo =
      caixa?.querySelector<HTMLElement>('input, textarea') ?? caixa?.querySelector<HTMLElement>('button');
    alvo?.focus();
    caixa?.scrollIntoView?.({ block: 'nearest' });
    // Só na entrada do critério.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alternar = (valor: string, rotulo?: string) => {
    const tem = cond.valores.includes(valor);
    const rotulos = { ...(cond.rotulos ?? {}) };
    if (tem) delete rotulos[valor];
    else if (rotulo) rotulos[valor] = rotulo;
    onChange({
      ...cond,
      valores: tem ? cond.valores.filter((v) => v !== valor) : [...cond.valores, valor],
      rotulos,
    });
  };

  return (
    <div
      className={cn('space-y-2 rounded-md border p-3', incompleto && 'border-destructive')}
      role="group"
      aria-labelledby={`${id}-titulo`}
      data-erro-regra={incompleto || undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p id={`${id}-titulo`} className="text-sm font-medium">
            {ROTULO_DO_CAMPO[cond.campo]}
          </p>
          {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 sm:h-8 sm:w-8"
          onClick={onRemover}
          aria-label={`Tirar o critério ${ROTULO_DO_CAMPO[cond.campo]}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      <div ref={valoresRef}>
        {cond.campo === 'IA' ? (
          <CriterioDaIa id={id} cond={cond} onChange={onChange} incompleto={!!incompleto} iaOriginal={iaOriginal} />
        ) : (
          <ValoresDoCampo id={id} cond={cond} onChange={onChange} alternar={alternar} membros={membros} />
        )}
      </div>
      {/* A faixa invertida já se explica nos campos dela. */}
      {incompleto && !faixaInvertida(cond) && (
        <p role="alert" id={`${id}-erro`} className="text-xs text-destructive">
          {cond.campo === 'VALOR'
            ? 'Informe o mínimo, o máximo ou os dois.'
            : cond.campo === 'IA'
              ? `Descreva o critério com ${CRITERIO_IA_MINIMO} a ${CRITERIO_IA_MAXIMO} caracteres.`
              : 'Escolha ao menos um valor.'}
        </p>
      )}
    </div>
  );
}

interface ValoresProps {
  id: string;
  cond: CondicaoRegra;
  onChange: (c: CondicaoRegra) => void;
  alternar: (valor: string, rotulo?: string) => void;
  membros: EscritorioMembro[];
}

function ValoresDoCampo({ id, cond, onChange, alternar, membros }: ValoresProps) {
  const fixos = (opcoes: { valor: string; rotulo: string }[]) => (
    <div className="flex flex-wrap gap-2">
      {opcoes.map((o) => (
        <Opcao key={o.valor} ativa={cond.valores.includes(o.valor)} onClick={() => alternar(o.valor)}>
          {o.rotulo}
        </Opcao>
      ))}
    </div>
  );

  switch (cond.campo) {
    case 'JUSTICA':
      return fixos(JUSTICAS);
    case 'ORIGEM':
      return fixos(ORIGENS);
    case 'POLO_DO_CLIENTE':
      return fixos(POLOS);
    case 'UF':
      return fixos(UFS.map((uf) => ({ valor: uf, rotulo: uf })));
    case 'DONO': {
      const deFora = cond.valores.filter((v) => !membros.some((m) => m.usuario_id === v));
      return (
        <div className="space-y-2">
          {deFora.length > 0 && (
            <Escolhidos cond={{ ...cond, valores: deFora }} alternar={alternar} />
          )}
          <div className="flex flex-wrap gap-2">
          {membros.map((m) => (
            <Opcao
              key={m.usuario_id}
              ativa={cond.valores.includes(m.usuario_id)}
              onClick={() => alternar(m.usuario_id, m.usuario_nome)}
            >
              {m.usuario_nome}
            </Opcao>
          ))}
          </div>
        </div>
      );
    }
    case 'VALOR':
      return <FaixaDeValor id={id} cond={cond} onChange={onChange} />;
    case 'CLASSE':
    case 'ASSUNTO':
    case 'CLIENTE':
      return (
        <div className="space-y-2">
          <Escolhidos cond={cond} alternar={alternar} />
          <BuscaDeValor id={id} cond={cond} alternar={alternar} />
        </div>
      );
    default:
      return (
        <div className="space-y-2">
          {(cond.campo === 'PARTE_NOME' || cond.campo === 'PARTE_DOCUMENTO') && (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Polo da parte">
              <Opcao ativa={!cond.polo} onClick={() => onChange({ ...cond, polo: null })}>
                Qualquer polo
              </Opcao>
              {POLOS.map((p) => (
                <Opcao
                  key={p.valor}
                  ativa={cond.polo === p.valor}
                  onClick={() => onChange({ ...cond, polo: p.valor as 'AUTOR' | 'REU' })}
                >
                  {p.rotulo}
                </Opcao>
              ))}
            </div>
          )}
          <Escolhidos cond={cond} alternar={alternar} />
          <TextoLivre id={id} cond={cond} alternar={alternar} />
        </div>
      );
  }
}

export const CRITERIO_IA_MINIMO = 10;
export const CRITERIO_IA_MAXIMO = 500;

/** O critério como o servidor o conta e grava: espaços e quebras de linha colapsados. */
export function normalizarCriterio(texto: string): string {
  return texto.replace(/\s+/g, ' ').trim();
}

/** O critério da IA está pronto para salvar (o servidor recusa fora do tamanho). */
export function criterioIaValido(c: CondicaoRegra): boolean {
  const texto = normalizarCriterio(c.valores[0] ?? '');
  return texto.length >= CRITERIO_IA_MINIMO && texto.length <= CRITERIO_IA_MAXIMO;
}

/** O critério em texto livre: o gestor descreve o tipo de processo, como diria a um colega. */
function CriterioDaIa({
  id,
  cond,
  onChange,
  incompleto,
  iaOriginal,
}: {
  id: string;
  cond: CondicaoRegra;
  onChange: (c: CondicaoRegra) => void;
  incompleto: boolean;
  iaOriginal?: string;
}) {
  const texto = cond.valores[0] ?? '';
  const tamanho = normalizarCriterio(texto).length;
  const mudou = !!iaOriginal && normalizarCriterio(iaOriginal) !== normalizarCriterio(texto);
  const descricoes = [`${id}-ia-ajuda`, `${id}-ia-tamanho`, mudou && `${id}-ia-mudou`, incompleto && `${id}-erro`]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="space-y-1">
      <Label htmlFor={`${id}-ia`} className="sr-only">
        Critério da IA
      </Label>
      <Textarea
        id={`${id}-ia`}
        value={texto}
        rows={3}
        maxLength={CRITERIO_IA_MAXIMO}
        aria-invalid={incompleto || undefined}
        aria-describedby={descricoes}
        placeholder="Ex.: ações contra plano de saúde por negativa de tratamento"
        onChange={(e) => onChange({ ...cond, valores: e.target.value ? [e.target.value] : [] })}
      />
      <p className="flex justify-between gap-2 text-xs text-muted-foreground">
        <span id={`${id}-ia-ajuda`}>Nomes de pessoas físicas não são enviados à IA.</span>
        <span id={`${id}-ia-tamanho`} className="shrink-0 tabular-nums">
          {tamanho}/{CRITERIO_IA_MAXIMO}
          <span className="sr-only"> caracteres</span>
        </span>
      </p>
      {mudou && (
        <p id={`${id}-ia-mudou`} className="text-xs text-muted-foreground">
          Mudar o texto faz a IA avaliar a fila de novo (gasta créditos).
        </p>
      )}
    </div>
  );
}

function Escolhidos({ cond, alternar }: { cond: CondicaoRegra; alternar: (v: string) => void }) {
  if (!cond.valores.length) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {cond.valores.map((v) => (
        <li key={v} className="flex items-center gap-1 rounded-md bg-muted py-0.5 pl-2 text-sm">
          <span className="[overflow-wrap:anywhere]">{rotuloDoValor(cond, v)}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 sm:h-7 sm:w-7"
            onClick={() => alternar(v)}
            aria-label={`Tirar ${rotuloDoValor(cond, v)}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

const EXEMPLO: Partial<Record<CondicaoRegra['campo'], string>> = {
  PARTE_NOME: 'Ex.: INSS',
  PARTE_DOCUMENTO: 'CPF ou CNPJ',
  ORGAO: 'Ex.: Vara Federal',
  TRIBUNAL: 'Ex.: TJSP',
  MUNICIPIO: 'Código IBGE (7 dígitos)',
};

/** O que o servidor recusaria, dito antes de entrar na lista. */
function recusaDoTexto(campo: CondicaoRegra['campo'], texto: string): string | null {
  const digitos = texto.replace(/\D/g, '');
  if (campo === 'PARTE_DOCUMENTO' && digitos.length !== 11 && digitos.length !== 14) {
    return 'Um CPF tem 11 dígitos e um CNPJ, 14.';
  }
  if (campo === 'MUNICIPIO' && digitos.length !== 7) return 'O código IBGE do município tem 7 dígitos.';
  return null;
}

function TextoLivre({ id, cond, alternar }: { id: string; cond: CondicaoRegra; alternar: (v: string) => void }) {
  const [texto, setTexto] = useState('');
  const [recusa, setRecusa] = useState<string | null>(null);
  const adicionar = () => {
    const v = texto.trim();
    if (!v) return;
    const motivo = recusaDoTexto(cond.campo, v);
    // Recusado, o texto fica no campo para o gestor corrigir.
    setRecusa(motivo);
    if (motivo) return;
    if (!cond.valores.includes(v)) alternar(v);
    setTexto('');
  };
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Label htmlFor={`${id}-texto`} className="sr-only">
          {ROTULO_DO_CAMPO[cond.campo]}
        </Label>
        <Input
          id={`${id}-texto`}
          value={texto}
          placeholder={EXEMPLO[cond.campo]}
          aria-invalid={!!recusa}
          aria-describedby={recusa ? `${id}-texto-erro` : undefined}
          onChange={(e) => {
            setTexto(e.target.value);
            setRecusa(null);
          }}
          onBlur={adicionar}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionar();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-11 shrink-0 sm:h-9"
          onClick={adicionar}
          disabled={!texto.trim()}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          Adicionar
        </Button>
      </div>
      {recusa && (
        <p id={`${id}-texto-erro`} role="alert" className="text-xs text-destructive">
          {recusa}
        </p>
      )}
    </div>
  );
}

function BuscaDeValor({
  id,
  cond,
  alternar,
}: {
  id: string;
  cond: CondicaoRegra;
  alternar: (v: string, rotulo?: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const termo = useDebounce(busca.trim(), 300);
  const resultados = useQuery({
    queryKey: ['regras-distribuicao', 'busca', cond.campo, termo],
    queryFn: async () => {
      if (cond.campo === 'CLIENTE') {
        // Com o CPF/CNPJ: quem está em dois escritórios pode ter dois "Maria Silva".
        return (await searchClientes(termo)).map((c) => ({
          value: c.id,
          label: c.cpf_ou_cnpj ? `${c.nome} (${c.cpf_ou_cnpj})` : c.nome,
        }));
      }
      const tipo = cond.campo === 'CLASSE' ? 'classes' : 'assuntos';
      return (await buscarTpu(tipo, termo)).map((i) => ({ value: i.codigo_cnj, label: i.nome }));
    },
    enabled: termo.length >= 2,
    staleTime: 60_000,
  });
  const opcoes = (resultados.data ?? []).filter((o) => !cond.valores.includes(o.value));
  const nome = ROTULO_DO_CAMPO[cond.campo].toLowerCase();
  return (
    <Combobox
      id={`${id}-busca`}
      className="h-11 sm:h-9"
      options={opcoes}
      value=""
      onValueChange={(v) => {
        const escolhida = opcoes.find((o) => o.value === v);
        if (escolhida) alternar(escolhida.value, escolhida.label);
        setBusca('');
      }}
      placeholder={`Buscar ${nome}…`}
      searchPlaceholder={cond.campo === 'CLIENTE' ? 'Nome ou CPF/CNPJ' : 'Nome ou código'}
      emptyMessage={termo.length < 2 ? 'Digite ao menos 2 letras' : 'Nada encontrado'}
      shouldFilter={false}
      searchValue={busca}
      onSearchChange={setBusca}
      loading={resultados.isFetching}
      renderOption={(o) => (
        <span className="[overflow-wrap:anywhere]">
          {o.label}
          {cond.campo !== 'CLIENTE' && <span className="ml-1 text-xs text-muted-foreground">({o.value})</span>}
        </span>
      )}
    />
  );
}

function FaixaDeValor({
  id,
  cond,
  onChange,
}: {
  id: string;
  cond: CondicaoRegra;
  onChange: (c: CondicaoRegra) => void;
}) {
  // O que o gestor digita fica como ele digitou ("1.000.000"): reformatar a cada tecla
  // transformava "1.000" em "1000" e o milhar seguinte virava decimal.
  const [textos, setTextos] = useState({
    minimo: valorParaTexto(cond.minimo),
    maximo: valorParaTexto(cond.maximo),
  });
  const campo = (chave: 'minimo' | 'maximo', rotulo: string, vazio: string) => {
    const texto = textos[chave];
    const invalido = texto.trim() !== '' && !valorValido(limparValor(texto));
    return (
      <div className="space-y-1">
        <Label htmlFor={`${id}-${chave}`}>{rotulo}</Label>
        <Input
          id={`${id}-${chave}`}
          inputMode="decimal"
          value={texto}
          aria-invalid={invalido}
          aria-describedby={invalido ? `${id}-${chave}-erro` : undefined}
          onChange={(e) => {
            const novo = e.target.value;
            setTextos((t) => ({ ...t, [chave]: novo }));
            const limpo = limparValor(novo);
            // Inválido vai como texto que o editor reconhece (valorValido) e segura o salvar.
            onChange({ ...cond, [chave]: limpo });
          }}
          placeholder={vazio}
        />
        {invalido && (
          <p id={`${id}-${chave}-erro`} className="text-xs text-destructive">
            Use um valor como 50.000 ou 1.234,56
          </p>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-2">
        {campo('minimo', 'A partir de (R$)', 'Sem mínimo')}
        {campo('maximo', 'Até (R$)', 'Sem máximo')}
      </div>
      {faixaInvertida(cond) && (
        <p role="alert" className="text-xs text-destructive">
          O mínimo passa do máximo.
        </p>
      )}
    </div>
  );
}

/** Mínimo maior que o máximo, os dois válidos: o servidor recusa a faixa. */
export function faixaInvertida(c: CondicaoRegra): boolean {
  return valorValido(c.minimo) && valorValido(c.maximo) && Number(c.minimo) > Number(c.maximo);
}

/** "1000000.5" → "1.000.000,50" para o campo (o que veio do servidor). */
function valorParaTexto(valor?: string | null): string {
  if (!valor) return '';
  const n = Number(valor);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : valor;
}

/** Um valor que o servidor aceita: número com até 2 casas. */
export function valorValido(valor: string | null | undefined): boolean {
  return !!valor && /^\d+(\.\d{1,2})?$/.test(valor);
}

/** Como se escreve no Brasil: "1.000.000,50" → "1000000.50", "1.000" → "1000"; vazio → null. */
export function limparValor(texto: string): string | null {
  const t = texto.replace(/[^\d,.]/g, '');
  if (!t) return null;
  if (t.includes(',')) return t.replace(/\./g, '').replace(',', '.');
  // Só pontos em grupos de três: separador de milhar, não decimal.
  return /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
}
